import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
// Leaf modules, not the `~/posthogs/events` barrel: consumers' tests mock that barrel with a
// factory, and a barrel import here would fail them on an export the factory does not declare.
import {
  fireShiftRequestAbandoned,
  type TShiftRequestExitTrigger,
} from '~/posthogs/events/ShiftRequest/abandoned';
import {
  fireShiftRequestAutoconfirmToggleAttempted,
  type TAutoConfirmToggleName,
} from '~/posthogs/events/ShiftRequest/autoconfirmToggleAttempted';
import {
  getShiftRequestStepIndex,
  isLaterShiftRequestStep,
  type TShiftRequestFlowStep,
} from '~/posthogs/events/ShiftRequest/flowSteps';
import { type IShiftDetailsFormValues } from './ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';

export interface IShiftRequestFlowTracking {
  /** Records how far the office got. Back-navigation never rewinds it. */
  recordStep: (step: TShiftRequestFlowStep) => void;
  /** Attributes the next exit to something more specific than a plain route change. */
  recordExitTrigger: (trigger: TShiftRequestExitTrigger) => void;
  /** Marks the request posted, so leaving afterwards is a completion rather than an abandonment. */
  markSubmitted: () => void;
  /** Reports a click on an Auto-Confirm toggle the last-minute experiment has locked on. */
  recordAutoConfirmToggleAttempt: (toggle: TAutoConfirmToggleName) => void;
}

const NOOP_TRACKING: IShiftRequestFlowTracking = {
  recordStep: () => {},
  recordExitTrigger: () => {},
  markSubmitted: () => {},
  recordAutoConfirmToggleAttempt: () => {},
};

const ShiftRequestFlowTrackingContext = createContext<IShiftRequestFlowTracking>(NOOP_TRACKING);

/**
 * Steps report their progress through this. Outside the wizard every call is a no-op, so shared
 * consumers like the Auto-Confirm field need not know whether they are in a tracked flow.
 */
export const useShiftRequestFlowTracking = (): IShiftRequestFlowTracking =>
  useContext(ShiftRequestFlowTrackingContext);

/** Written on a successful post so a sibling tab does not report the same request as abandoned. */
const COMPLETION_STORAGE_KEY = 'shift_request:last_completed_at';

const readLastCompletedAt = (): number => {
  try {
    return Number(window.localStorage.getItem(COMPLETION_STORAGE_KEY)) || 0;
  } catch {
    // Blocked storage throws; treat as "no completion seen".
    return 0;
  }
};

const writeLastCompletedAt = (at: number): void => {
  try {
    window.localStorage.setItem(COMPLETION_STORAGE_KEY, String(at));
  } catch {
    // Only the cross-tab guard degrades; the in-tab one is independent of storage.
  }
};

/**
 * Owns one visit to the shift request wizard (GOTU-5396, GOTU-5437). Mounted by
 * `ShiftRequestLayout`, which spans every step, so its lifetime is exactly one pass through the
 * flow — re-entering starts a fresh visit with its own counters.
 */
export const ShiftRequestFlowTrackingProvider = ({ children }: { children: ReactNode }) => {
  const { getValues } = useFormContext<IShiftDetailsFormValues>();

  // Lazy initializer so the clock is read once, on mount, rather than on every render.
  const [startedAt] = useState(() => Date.now());

  const lastStepRef = useRef<TShiftRequestFlowStep | null>(null);
  const exitTriggerRef = useRef<TShiftRequestExitTrigger>('navigation');
  const submittedRef = useRef(false);
  const abandonedRef = useRef(false);
  const toggleAttemptsRef = useRef(0);

  const abandon = useCallback(
    (trigger: TShiftRequestExitTrigger) => {
      const lastStep = lastStepRef.current;
      // No step reached means the flow was never entered; the rest keep a completed or
      // already-reported visit from counting as a drop-off.
      if (!lastStep || submittedRef.current || abandonedRef.current) return;
      // Posting in a sibling tab ends the request, so closing this one is not an abandonment.
      if (readLastCompletedAt() > startedAt) return;

      abandonedRef.current = true;

      const { professionType, dates } = getValues();
      fireShiftRequestAbandoned({
        lastStepReached: lastStep,
        lastStepIndex: getShiftRequestStepIndex(lastStep),
        exitTrigger: trigger,
        professionalTypeSelected: professionType || undefined,
        datesSelected: Boolean(dates?.length),
        timeInFlowSeconds: Math.round((Date.now() - startedAt) / 1000),
      });
    },
    [getValues, startedAt],
  );

  // Held in a ref so the effects below never re-run on identity change — a cleanup firing then
  // would report an abandonment mid-flow. Synced in an effect rather than during render, which
  // React may replay or discard.
  const abandonRef = useRef(abandon);
  useEffect(() => {
    abandonRef.current = abandon;
  }, [abandon]);

  useEffect(() => {
    const handleBeforeUnload = () => abandonRef.current('page_unload');
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => () => abandonRef.current(exitTriggerRef.current), []);

  const tracking = useMemo<IShiftRequestFlowTracking>(
    () => ({
      recordStep: (step) => {
        if (isLaterShiftRequestStep(step, lastStepRef.current)) lastStepRef.current = step;
      },
      recordExitTrigger: (trigger) => {
        exitTriggerRef.current = trigger;
      },
      markSubmitted: () => {
        submittedRef.current = true;
        writeLastCompletedAt(Date.now());
      },
      recordAutoConfirmToggleAttempt: (toggle) => {
        toggleAttemptsRef.current += 1;
        fireShiftRequestAutoconfirmToggleAttempted({
          officeId: getValues('officeId'),
          toggle,
          attemptCount: toggleAttemptsRef.current,
        });
      },
    }),
    [getValues],
  );

  return (
    <ShiftRequestFlowTrackingContext.Provider value={tracking}>
      {children}
    </ShiftRequestFlowTrackingContext.Provider>
  );
};
